import React, { useEffect, useRef } from 'react';
import { Eraser } from 'lucide-react';

interface Props {
    value?: string;
    onChange: (dataUrl: string) => void;
    onClear?: () => void;
}

/** Zone de dessin pour une signature manuscrite (export PNG base64). */
const SignaturePad: React.FC<Props> = ({ value, onChange, onClear }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawing = useRef(false);
    const last = useRef<{ x: number; y: number } | null>(null);
    const skipLoad = useRef(false);

    const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current!;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    };

    const paintBackground = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
    };

    useEffect(() => {
        if (skipLoad.current) {
            skipLoad.current = false;
            return;
        }
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        paintBackground(ctx, canvas.width, canvas.height);
        if (!value) return;
        const img = new Image();
        img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        img.src = value;
    }, [value]);

    const emit = (dataUrl: string) => {
        skipLoad.current = true;
        onChange(dataUrl);
    };

    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.setPointerCapture(e.pointerId);
        drawing.current = true;
        last.current = getPos(e);
    };

    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawing.current || !last.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        const pos = getPos(e);
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(last.current.x, last.current.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        last.current = pos;
    };

    const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawing.current) return;
        drawing.current = false;
        last.current = null;
        try {
            canvasRef.current?.releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
        const canvas = canvasRef.current;
        if (canvas) emit(canvas.toDataURL('image/png'));
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        paintBackground(ctx, canvas.width, canvas.height);
        emit('');
        onClear?.();
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-500">Dessinez votre signature ci-dessous</p>
                <button
                    type="button"
                    onClick={clear}
                    className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 px-2 py-1 rounded border border-slate-200"
                >
                    <Eraser className="w-3.5 h-3.5" /> Effacer
                </button>
            </div>
            <canvas
                ref={canvasRef}
                width={320}
                height={120}
                className="w-full h-[120px] border border-dashed border-slate-300 rounded-lg bg-white touch-none cursor-crosshair"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                aria-label="Zone de signature manuscrite"
            />
        </div>
    );
};

export default SignaturePad;
